import React, { useState } from 'react';
import {
  X,
  CheckCircle2,
  MapPin,
  MessageSquare,
  LinkIcon,
  Sparkles,
  Trophy,
  Share2,
} from 'lucide-react';
import { Lounge, Badge, BADGES } from '../data/lounges';

interface CheckInModalViewProps {
  lounge: Lounge;
  onCheckIn: (note?: string, linkedNoteId?: string) => void;
  onClose: () => void;
  recentNotes?: { id: string; brand: string; name: string }[];
  newBadgesEarned?: Badge[];
  totalCheckIns: number;
  on7CLPromo?: () => void;
}

const CheckInModalView: React.FC<CheckInModalViewProps> = ({
  lounge,
  onCheckIn,
  onClose,
  recentNotes = [],
  newBadgesEarned = [],
  totalCheckIns,
  on7CLPromo,
}) => {
  const [note, setNote] = useState('');
  const [linkedNoteId, setLinkedNoteId] = useState<string | undefined>();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckIn = async () => {
    setIsSubmitting(true);
    // Simulate check-in
    await new Promise((resolve) => setTimeout(resolve, 500));
    onCheckIn(note || undefined, linkedNoteId);
    setShowSuccess(true);
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-stone-900 rounded-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-300">
          {/* Success animation */}
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-500">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">Checked In!</h2>
            <p className="text-stone-400">{lounge.name}</p>

            {/* New badges earned */}
            {newBadgesEarned.length > 0 && (
              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <span className="text-amber-500 font-semibold">
                    {newBadgesEarned.length === 1 ? 'New Badge Earned!' : `${newBadgesEarned.length} New Badges!`}
                  </span>
                </div>
                <div className="flex justify-center gap-3">
                  {newBadgesEarned.map((badge) => (
                    <div key={badge.id} className="text-center">
                      <div className="w-14 h-14 bg-stone-800 rounded-full flex items-center justify-center text-2xl mb-1 mx-auto">
                        {badge.icon}
                      </div>
                      <p className="text-xs text-white font-medium">{badge.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 7CL Promo - Show after 5+ check-ins */}
            {totalCheckIns >= 5 && on7CLPromo && (
              <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                  <div className="text-left flex-1">
                    <p className="text-sm text-white font-medium">
                      {totalCheckIns} lounge visits this month!
                    </p>
                    <p className="text-xs text-stone-400">
                      Keep your wardrobe smoke-free with 7CL
                    </p>
                  </div>
                </div>
                <button
                  onClick={on7CLPromo}
                  className="w-full mt-3 py-2 bg-amber-500/20 text-amber-500 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-colors"
                >
                  Learn About 7CL
                </button>
              </div>
            )}

            {/* Share check-in */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-stone-800 text-white rounded-xl font-medium"
              >
                Done
              </button>
              <button className="px-4 py-3 bg-stone-800 text-white rounded-xl">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center z-50">
      <div className="bg-stone-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-800">
          <h2 className="text-lg font-semibold text-white">Check In</h2>
          <button onClick={onClose} className="p-2 text-stone-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {/* Lounge info */}
          <div className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-xl mb-4">
            <div className="w-12 h-12 bg-stone-700 rounded-lg flex items-center justify-center text-xl">
              🚬
            </div>
            <div>
              <h3 className="font-semibold text-white">{lounge.name}</h3>
              <p className="text-sm text-stone-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {lounge.city}, {lounge.state}
              </p>
            </div>
          </div>

          {/* Optional note */}
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-stone-400 mb-2">
              <MessageSquare className="w-4 h-4" />
              Add a note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What are you smoking tonight?"
              className="w-full p-3 bg-stone-800 border border-stone-700 rounded-xl text-white placeholder-stone-500 resize-none focus:outline-none focus:border-amber-500"
              rows={2}
            />
          </div>

          {/* Link to tasting note */}
          {recentNotes.length > 0 && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-stone-400 mb-2">
                <LinkIcon className="w-4 h-4" />
                Link a recent cigar (optional)
              </label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {recentNotes.map((recentNote) => (
                  <button
                    key={recentNote.id}
                    onClick={() =>
                      setLinkedNoteId(linkedNoteId === recentNote.id ? undefined : recentNote.id)
                    }
                    className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm transition-colors ${
                      linkedNoteId === recentNote.id
                        ? 'bg-amber-500 text-stone-900'
                        : 'bg-stone-800 text-stone-300'
                    }`}
                  >
                    {recentNote.brand} {recentNote.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Badge progress teaser */}
          <div className="p-3 bg-stone-800/30 rounded-xl mb-4">
            <p className="text-xs text-stone-500 mb-2">Badges you're working toward:</p>
            <div className="flex gap-3">
              {BADGES.slice(0, 3).map((badge) => (
                <div key={badge.id} className="text-center opacity-50">
                  <div className="w-10 h-10 bg-stone-800 rounded-full flex items-center justify-center text-lg mx-auto mb-1">
                    {badge.icon}
                  </div>
                  <p className="text-xs text-stone-500">{badge.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Check in button */}
          <button
            onClick={handleCheckIn}
            disabled={isSubmitting}
            className="w-full py-3 bg-amber-500 text-stone-900 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-stone-900/30 border-t-stone-900 rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Check In to {lounge.name}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckInModalView;
